/* ============================================================================
   dataJsonLint — validación DETERMINISTA de un data.json generado.

   Caza los bugs que hacen que "lo que está en el form no se tome":
   el runtime reemplaza [placeholder] por '' si no matchea ningún campo, así
   que un mask con placeholder mal escrito emite el comando SIN el valor.

   Devuelve diagnostics con el mismo shape que sstDiagnostics/coverageCheck:
   { code, severity: 'error'|'warning'|'info', message, raw }
   → compatibles con DiagnosticsModal.
   ============================================================================ */

import { normalizeRemarkType, resolveRemarkSemantics } from './remarkSemantics'

const SYSTEM_TOKENS = /^@?(DATE|DATE_FULL|TIME|YEAR|PNR|AAA|INITIALS|AGENT|BTN_PRSD|INITS|SINE)$/i
const DIRECTIVES = /^(if|else|endif|read|compute|loop)$/i
const NO_VALUE_TYPES = ['label', 'button', 'button_menu', 'table']

function norm(k) {
  return String(k == null ? '' : k).trim().toLowerCase()
}

function fieldKeys(f) {
  return [f && f.campoForm, f && f.tag, f && f.id].map(norm).filter(Boolean)
}

function allFields(dataJson) {
  const forms = (dataJson && Array.isArray(dataJson.forms) && dataJson.forms) || []
  const out = []
  forms.forEach((form, fi) => {
    const remarks = (form && Array.isArray(form.remarks) && form.remarks) || []
    remarks.forEach((r, ri) => out.push({ field: r, form: form.form || 'form' + (fi + 1), pos: ri }))
  })
  return out
}

function onLoadMapForField(dataJson, field, target = '') {
  const keys = new Set(fieldKeys(field))
  if (!keys.size) return null
  for (const step of (Array.isArray(dataJson?.onLoad) ? dataJson.onLoad : [])) {
    for (const map of (Array.isArray(step?.map) ? step.map : [])) {
      if (!keys.has(norm(map?.tag))) continue
      if (!target || norm(map?.target) === norm(target)) return map
    }
  }
  return null
}

function hasDatasourceProvider(dataJson, field) {
  if (String(field?.datasource_ref || field?.catalog_ref || '').trim()) return true
  if (field?.catalog && field.catalog.pcc) return true
  const map = onLoadMapForField(dataJson, field)
  return Boolean(map && ['datasource', 'options'].includes(norm(map.target)) && map.source)
}

/* Junta TODO el texto ejecutable donde puede haber placeholders: masks de
   fields, comandos globales, comandos de acciones/formularios y alerts. */
function commandTexts(dataJson) {
  const texts = []
  allFields(dataJson).forEach(({ field }) => {
    if (field && field.remark) texts.push(String(field.remark))
    if (field && field.remark_mask) texts.push(String(field.remark_mask))
    if (field && field.dato) texts.push(String(field.dato))
  })
  const walk = (v) => {
    if (v == null) return
    if (typeof v === 'string') { texts.push(v); return }
    if (Array.isArray(v)) { v.forEach(walk); return }
    if (typeof v === 'object') Object.values(v).forEach(walk)
  }
  walk(dataJson && dataJson.postCommands)
  ;(Array.isArray(dataJson?.actions) ? dataJson.actions : []).forEach((action) => {
    walk(action?.commands)
  })
  ;(Array.isArray(dataJson?.forms) ? dataJson.forms : []).forEach((form) => {
    const source = form?.source_additional
    if (!source || typeof source !== 'object' || Array.isArray(source)) return
    walk(source.after_form_commands)
    walk(source.commands)
  })
  walk(dataJson && dataJson.alerts)
  return texts
}

function placeholdersIn(text) {
  const out = []
  const s = String(text == null ? '' : text)
  let m
  const rxSq = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g
  while ((m = rxSq.exec(s))) out.push({ name: m[1], raw: m[0] })
  const rxBr = /\{\{?([A-Za-z_][A-Za-z0-9_]*)\}?\}/g
  while ((m = rxBr.exec(s))) {
    if (!DIRECTIVES.test(m[1]) && m[1] !== '__PNR__') out.push({ name: m[1], raw: m[0] })
  }
  return out
}

function similarity(a, b) {
  a = norm(a); b = norm(b)
  if (!a || !b) return 0
  if (a === b) return 1
  if (a.indexOf(b) >= 0 || b.indexOf(a) >= 0) return 0.8
  const as = a.replace(/_/g, ''); const bs = b.replace(/_/g, '')
  if (as === bs) return 0.9
  let hits = 0
  const seen = new Set(bs.split(''))
  as.split('').forEach((ch) => { if (seen.has(ch)) hits++ })
  return hits / Math.max(as.length, bs.length)
}

function closestKey(name, keys) {
  let best = ''
  let score = 0
  keys.forEach((k) => {
    const s = similarity(name, k)
    if (s > score) { score = s; best = k }
  })
  return score >= 0.6 ? best : ''
}

export function lintDataJson(dataJson) {
  const diags = []
  if (!dataJson || typeof dataJson !== 'object') {
    return [{ code: 'LINT_INVALID_JSON', severity: 'error', message: 'data.json inválido o vacío.', raw: '' }]
  }

  const entries = allFields(dataJson)
  const keySet = new Set()
  entries.forEach(({ field }) => fieldKeys(field).forEach((k) => keySet.add(k)))
  const keys = Array.from(keySet)
  const texts = commandTexts(dataJson)

  // R1: placeholder que no matchea ningún campo → el runtime lo reemplaza por VACÍO
  const reported = new Set()
  texts.forEach((t) => {
    placeholdersIn(t).forEach(({ name, raw }) => {
      if (SYSTEM_TOKENS.test(name)) return
      if (keySet.has(norm(name))) return
      const dedup = norm(name)
      if (reported.has(dedup)) return
      reported.add(dedup)
      const sug = closestKey(name, keys)
      diags.push({
        code: 'LINT_PLACEHOLDER_UNKNOWN',
        severity: 'error',
        message:
          'El placeholder ' + raw + ' no corresponde a ningún campo (tag/campoForm). El runtime lo reemplaza por VACÍO y el comando sale sin ese valor.' +
          (sug ? ' ¿Quisiste decir [' + sug + ']?' : ' Campos disponibles: ' + keys.slice(0, 12).join(', ') + '.'),
        raw: t.length > 160 ? t.slice(0, 160) + '…' : t,
      })
    })
  })

  // R2: campo cuyo valor NUNCA se emite (no aparece en ningún mask/postCommand/alert)
  const usedKeys = new Set()
  texts.forEach((t) => placeholdersIn(t).forEach(({ name }) => usedKeys.add(norm(name))))
  entries.forEach(({ field, form }) => {
    const td = norm(field && field.typedata)
    if (NO_VALUE_TYPES.indexOf(td) >= 0) return
    const automaticDisplay = (field?.readonly === true || field?.readOnly === true) && onLoadMapForField(dataJson, field)
    if (automaticDisplay) return
    if (field && field.skip === true && field.dato) return // oculto con auto-valor: puede emitirse vía su propio mask
    const kks = fieldKeys(field)
    if (!kks.length) return
    const isUsed = kks.some((k) => usedKeys.has(k))
    const ownMask = String((field && (field.remark_mask || field.remark)) || '').trim()
    // El caso runtime real: el field se emite por SU PROPIO mask; si su mask no
    // contiene su placeholder NI el de otro campo válido, el valor se pierde.
    const ownMaskUsesSelf = placeholdersIn(ownMask).some((p) => kks.indexOf(norm(p.name)) >= 0)
    if (!isUsed && !ownMaskUsesSelf) {
      diags.push({
        code: 'LINT_FIELD_UNUSED',
        severity: 'error',
        message:
          'El campo "' + (field.label || field.tag || field.campoForm) + '" (' + form + ') no aparece en ningún remark_mask ni postCommand: lo que el agente escriba ahí NUNCA llega a Sabre. Agregá [' + (field.tag || field.campoForm) + '] al mask correspondiente.',
        raw: ownMask,
      })
    } else if (!ownMask && !isUsed) {
      diags.push({
        code: 'LINT_EMPTY_MASK',
        severity: 'warning',
        message: 'El campo "' + (field.label || field.tag) + '" (' + form + ') no tiene remark/remark_mask.',
        raw: '',
      })
    }
  })

  // R2c: el tipo visible, el block y el prefijo deben representar la misma
  // instrucción. Evita SSR enviados como 5 y PE convertido en 5PE¥.
  entries.forEach(({ field, form }) => {
    const semantic = resolveRemarkSemantics(field)
    const currentType = normalizeRemarkType(field && field.typeremark)
    const currentBlock = field && field.block !== undefined && field.block !== '' ? Number(field.block) : null
    if (semantic.kind === 'pe') {
      if (currentType || currentBlock !== null) {
        diags.push({
          code: 'LINT_PE_AS_REMARK',
          severity: 'error',
          message: 'El campo "' + (field.label || field.tag) + '" (' + form + ') usa PE¥, que es un comando directo. No debe tener typeremark ni block; de lo contrario puede enviarse como 5PE¥.',
          raw: String(field.remark_mask || field.remark || ''),
        })
      }
      return
    }
    if (!semantic.typeremark) return
    const typeConflicts = Boolean(currentType) && currentType !== semantic.typeremark
    const blockConflicts = currentBlock !== null && currentBlock !== semantic.block
    if (typeConflicts || blockConflicts) {
      diags.push({
        code: 'LINT_REMARK_SEMANTIC_MISMATCH',
        severity: 'error',
        message: 'El campo "' + (field.label || field.tag) + '" (' + form + ') representa ' + semantic.typeremark + ' y debe usar block ' + semantic.block + ', no typeremark="' + (field.typeremark || '') + '" / block=' + (field.block ?? 'vacío') + '.',
        raw: String(field.remark_mask || field.remark || ''),
      })
    }
  })

  // R2b: emisor SIN block y cuyo mask no arranca con prefijo Sabre valido
  // -> el runtime NO antepone nada y Sabre recibe un comando invalido
  // (visto en produccion: DOCS/... CTCE/... CTCM/... sin el 3).
  const VALID_PREFIX = /^([*]|[0-9][A-Z0-9\-./]|PE(?:[¥‡#¤]|$)|ER\b|ET\b|IR\b|EM\b|AAA\b|WP\b|W-|HK\d|I$|I\s|I\/)/i
  entries.forEach(({ field, form }) => {
    const mask = String((field && (field.remark_mask || field.remark)) || '').trim()
    if (!mask) return
    const b = Number(field && field.block)
    const hasBlock = field && field.block !== undefined && field.block !== '' && !Number.isNaN(b)
    if (!hasBlock && !VALID_PREFIX.test(mask.replace(/\[[^\]]+\]/g, 'X'))) {
      diags.push({
        code: 'LINT_EMITTER_NO_BLOCK',
        severity: 'error',
        message:
          'El emisor "' + (field.label || field.tag) + '" (' + form + ') tiene mask "' + mask.slice(0, 40) + '…" pero SIN block: el runtime no antepone el numero de linea y Sabre recibe un comando invalido. SSR (DOCS/CTCE/CTCM/OSI) => block 3; remarks => block 5.',
        raw: mask,
      })
    }
  })

  // R3: tag !== campoForm (el schema exige iguales)
  entries.forEach(({ field, form }) => {
    if (field && field.tag && field.campoForm && norm(field.tag) !== norm(field.campoForm)) {
      diags.push({
        code: 'LINT_TAG_MISMATCH',
        severity: 'warning',
        message: 'tag ("' + field.tag + '") y campoForm ("' + field.campoForm + '") difieren en ' + form + '. El schema exige que sean idénticos.',
        raw: '',
      })
    }
  })

  // R4: select sin datasource
  entries.forEach(({ field, form }) => {
    if (norm(field && field.typedata) === 'select') {
      const ds = field.datasource
      if ((!Array.isArray(ds) || ds.length === 0) && !hasDatasourceProvider(dataJson, field)) {
        diags.push({
          code: 'LINT_SELECT_NO_DATASOURCE',
          severity: 'error',
          message: 'El select "' + (field.label || field.tag) + '" (' + form + ') no tiene datasource: el agente no verá opciones.',
          raw: '',
        })
      } else {
        const seen = new Set()
        ds.forEach((o) => {
          const v = norm(o && o.value)
          if (seen.has(v) && v !== '') {
            diags.push({
              code: 'LINT_DUP_DATASOURCE',
              severity: 'warning',
              message: 'El select "' + (field.label || field.tag) + '" tiene el value duplicado "' + o.value + '".',
              raw: '',
            })
          }
          seen.add(v)
        })
      }
    }
  })

  // R5: nada obligatorio
  const anyMandatory = entries.some(({ field }) => field && field.mandatory === true)
  if (entries.length > 0 && !anyMandatory) {
    diags.push({
      code: 'LINT_NO_MANDATORY',
      severity: 'warning',
      message: 'Ningún campo es obligatorio (mandatory). Verificá si es intencional.',
      raw: '',
    })
  }

  if (!diags.length) {
    diags.push({
      code: 'LINT_OK',
      severity: 'info',
      message: 'Lint determinista OK: ' + entries.length + ' campos, todos los placeholders resuelven y todos los valores del form se emiten.',
      raw: '',
    })
  }
  return diags
}

export function lintErrors(diags) {
  return (Array.isArray(diags) ? diags : []).filter((d) => d && d.severity === 'error')
}

/* Texto listo para mandarle al agente 3 como corrección automática. */
export function lintFixPrompt(diags, dataJson) {
  const errs = lintErrors(diags)
  if (!errs.length) return ''
  return (
    'CORRECCIÓN AUTOMÁTICA REQUERIDA. El data.json que generaste tiene errores deterministas que hacen que los valores del formulario NO lleguen a Sabre. Corregí EXACTAMENTE esto y devolvé el data.json COMPLETO corregido (mismo schema de 24 propiedades, sin cambiar nada más):\n\n' +
    errs.map((d, i) => (i + 1) + '. ' + d.message).join('\n') +
    '\n\nDATA.JSON ACTUAL:\n' + JSON.stringify(dataJson)
  )
}
